/**
 * The advanced filter state, as clauses in the same AST the typed query
 * produces.
 *
 * Nothing new is sent to the engine except ``anyOf``: the multi-selects (artists,
 * genres, rotations as text; eras as year ranges) become ``anyOf`` clauses (the
 * typed language has no OR), and each range slider becomes an ordinary ``range``
 * clause. They are ANDed with whatever is typed, so the search box and the
 * filters are one query, filtered in one place -- the engine, across the whole
 * library.
 */

import type { Clause } from "./parse";
import type { RawToken } from "./tokenize";

export interface Era {
  label: string;
  from: number;
  to: number;
}

/** A curator column constraint: yes/no, a number range, or any of some words. */
export type CustomFilter =
  | { key: string; label: string; type: "bool"; value: boolean }
  | { key: string; label: string; type: "number"; value: [number, number] }
  | { key: string; label: string; type: "text"; value: string[] };

export interface Filters {
  artists: string[];
  /** null = not constrained. */
  bpm: [number, number] | null;
  energyLevel: [number, number] | null;
  danceability: [number, number] | null;
  /** Integrated loudness, LUFS. Optional so older saved states stay valid. */
  lufs?: [number, number] | null;
  /** Genre tags, any of. */
  genres?: string[];
  /** Decades of the year tag, any of. */
  eras?: Era[];
  /** Rotation categories (Nova track groups), any of. */
  rotations?: string[];
  /** The station's own columns, as ``custom:<key>`` clauses. */
  custom?: CustomFilter[];
}

export const EMPTY_FILTERS: Filters = {
  artists: [], bpm: null, energyLevel: null, danceability: null, lufs: null, genres: [], eras: [], rotations: [], custom: [],
};

/** The full span of each slider; a range equal to its bounds constrains nothing. */
export const BOUNDS = {
  bpm: [60, 200] as [number, number],
  energyLevel: [1, 5] as [number, number],
  danceability: [0, 1] as [number, number],
  lufs: [-30, -4] as [number, number],
};

/** Panel clauses have no source text; this marks them as not removable from the box. */
const SYNTHETIC: RawToken = { kind: "field", start: -1, end: -1, text: "", negated: false };

export function isActive(filters: Filters): boolean {
  return filterClauses(filters).length > 0;
}

const RANGES: [keyof typeof BOUNDS, string][] = [
  ["bpm", "bpm"],
  ["energyLevel", "energylevel"],
  ["danceability", "danceability"],
  ["lufs", "lufs"],
];

export function filterClauses(filters: Filters): Clause[] {
  const out: Clause[] = [];
  const anyOf = (field: string, values: string[] | undefined) => {
    if (values?.length) out.push({ field, type: "text", cmp: "anyOf", negated: false, value: [...values], token: SYNTHETIC });
  };
  anyOf("artist", filters.artists);
  anyOf("genre", filters.genres);
  anyOf("group", filters.rotations);
  if (filters.eras?.length) {
    out.push({ field: "year", type: "number", cmp: "anyOf", negated: false,
               value: filters.eras.map((e) => [e.from, e.to]) as unknown as string[], token: SYNTHETIC });
  }
  for (const [key, field] of RANGES) {
    const range = filters[key] ?? null;
    if (!range) continue;
    const [lo, hi] = BOUNDS[key];
    if (range[0] <= lo && range[1] >= hi) continue;
    out.push({ field, type: "number", cmp: "range", negated: false,
               value: range[0], value2: range[1], token: SYNTHETIC });
  }
  for (const c of filters.custom ?? []) {
    const field = `custom:${c.key}`;
    if (c.type === "bool") out.push({ field, type: "bool", cmp: "eq", negated: false, value: c.value, token: SYNTHETIC });
    else if (c.type === "number") out.push({ field, type: "number", cmp: "range", negated: false, value: c.value[0], value2: c.value[1], token: SYNTHETIC });
    else if (c.value.length) out.push({ field, type: "text", cmp: "anyOf", negated: false, value: [...c.value], token: SYNTHETIC });
  }
  return out;
}

/** One removable chip per filter value, in the order the bar shows them. */
export interface FilterChip {
  key: string;
  label: string;
  value: string;
  remove: (filters: Filters) => Filters;
}

const RANGE_LABEL: Record<keyof typeof BOUNDS, string> = {
  bpm: "BPM", energyLevel: "Energy", danceability: "Dance", lufs: "LUFS",
};

const trim = (n: number) => String(Math.round(n * 100) / 100);

export function filterChips(filters: Filters): FilterChip[] {
  const chips: FilterChip[] = [];
  for (const name of filters.artists) {
    chips.push({ key: `artist:${name}`, label: "Artist", value: name,
                 remove: (f) => ({ ...f, artists: f.artists.filter((a) => a !== name) }) });
  }
  for (const name of filters.genres ?? []) {
    chips.push({ key: `genre:${name}`, label: "Genre", value: name,
                 remove: (f) => ({ ...f, genres: (f.genres ?? []).filter((g) => g !== name) }) });
  }
  for (const era of filters.eras ?? []) {
    chips.push({ key: `era:${era.label}`, label: "Era", value: era.label,
                 remove: (f) => ({ ...f, eras: (f.eras ?? []).filter((e) => e.label !== era.label) }) });
  }
  for (const name of filters.rotations ?? []) {
    chips.push({ key: `rotation:${name}`, label: "Group", value: name,
                 remove: (f) => ({ ...f, rotations: (f.rotations ?? []).filter((r) => r !== name) }) });
  }
  for (const [key] of RANGES) {
    const range = filters[key] ?? null;
    if (!range) continue;
    const [lo, hi] = BOUNDS[key];
    if (range[0] <= lo && range[1] >= hi) continue;
    chips.push({ key, label: RANGE_LABEL[key], value: range[0] === range[1] ? trim(range[0]) : `${trim(range[0])}–${trim(range[1])}`,
                 remove: (f) => ({ ...f, [key]: null }) });
  }
  for (const c of filters.custom ?? []) {
    const value = c.type === "bool" ? (c.value ? "yes" : "no")
      : c.type === "number" ? `${trim(c.value[0])}–${trim(c.value[1])}` : c.value.join(" / ");
    chips.push({ key: `custom:${c.key}`, label: c.label, value,
                 remove: (f) => ({ ...f, custom: (f.custom ?? []).filter((x) => x.key !== c.key) }) });
  }
  return chips;
}
